/**
 * FloatingFarePill — compact live-fare overlay that floats on top of the
 * driver's ACTIVE NAVIGATION view (the in-app turn-by-turn DirectionsPanel)
 * during a trip_in_progress leg.
 *
 * WHY: drivers need to follow turn-by-turn directions AND keep an eye on the
 * running fare without leaving the navigation view. Previously the only way to
 * see the fare was to switch to the separate METER tab — which hid the
 * directions. This overlay keeps the navigation visible and shows a small pill
 * (`LIVE £43.62`) that expands in place into the full FareMeterPanel breakdown,
 * then collapses back to the pill. It sits at the BOTTOM-RIGHT so it never
 * covers the directions header (ETA / remaining distance) or the next-step
 * instruction at the top of the panel.
 *
 * SCOPE (strict): UI only.
 *   - Polls the SAME read-only `/driver/live-fare/{rideId}` endpoint the
 *     meter already uses — no new backend, no fare-calculation changes.
 *   - The expanded view reuses <FareMeterPanel/> verbatim so the
 *     breakdown stays a single source of truth.
 *   - Does NOT touch dispatch, lifecycle, offer routing, or the existing
 *     right-panel NAV/METER toggle (the full METER tab still works as before).
 *
 * Render it inside a `position: relative` host wrapped around the
 * DirectionsPanel so the absolute positioning anchors to the nav view.
 */
import React, { useEffect, useRef, useState } from "react";
import { ChevronUp, X } from "lucide-react";

import { api } from "../../lib/api";
import { FareMeterPanel } from "../FareMeterPanel";

const POLL_MS = 5000;

export function FloatingFarePill({ rideId, startedAt, fallbackEstimate }) {
  const [expanded, setExpanded] = useState(false);
  const [liveFare, setLiveFare] = useState(null);
  const pollRef = useRef(null);

  // Lightweight headline-only poll for the pill number. The expanded
  // FareMeterPanel runs its own poll for the detailed breakdown; this keeps
  // the compact pill live even while collapsed.
  useEffect(() => {
    let active = true;
    const fetchFare = async () => {
      if (!rideId) return;
      try {
        const res = await api.get(`/driver/live-fare/${rideId}`);
        if (active) setLiveFare(res?.data?.live_fare ?? null);
      } catch (e) {
        // 404/409 before started_at — keep last good value (or fallback).
      }
    };
    fetchFare();
    pollRef.current = setInterval(fetchFare, POLL_MS);
    return () => {
      active = false;
      clearInterval(pollRef.current);
    };
  }, [rideId]);

  const isLive = liveFare != null;
  const amount = isLive
    ? Number(liveFare)
    : fallbackEstimate != null
    ? Number(fallbackEstimate)
    : null;
  const amountLabel = amount != null ? `£${amount.toFixed(2)}` : "£—";

  if (expanded) {
    // Anchored bottom-right and grows upward, overlaying the navigation view.
    return (
      <div
        className="absolute bottom-2 right-2 z-20 w-[300px] max-w-[calc(100%-1rem)] max-h-[60vh] overflow-y-auto shadow-2xl"
        data-testid="floating-fare-expanded"
      >
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="absolute top-2 right-2 z-10 w-8 h-8 flex items-center justify-center bg-[#0A0A0A] border-2 border-[#10B981] text-[#22C55E] hover:bg-[#1F1F1F] transition-colors"
          data-testid="floating-fare-collapse-btn"
          aria-label="Collapse fare panel"
        >
          <X className="w-4 h-4" />
        </button>
        <FareMeterPanel
          rideId={rideId}
          startedAt={startedAt}
          fallbackEstimate={fallbackEstimate}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setExpanded(true)}
      className="absolute bottom-2 right-2 z-20 flex items-center gap-2 sm:gap-2.5 bg-[#0A0A0A] border-2 border-[#10B981] px-2.5 sm:px-3 py-2 shadow-lg hover:bg-[#1F1F1F] transition-colors"
      data-testid="floating-fare-pill"
      aria-label="Show live fare breakdown"
      aria-expanded={false}
    >
      <span className="flex items-center gap-1.5">
        <span
          className={`w-2 h-2 rounded-full bg-[#22C55E] ${isLive ? "pulse-active" : "animate-pulse"}`}
        />
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF]">
          {isLive ? "Live" : "Est"}
        </span>
      </span>
      <span
        className="font-led text-[#22C55E] text-base sm:text-xl leading-none"
        style={{ textShadow: "0 0 1px rgba(34,197,94,0.9), 0 0 5px rgba(34,197,94,0.35)" }}
        data-testid="floating-fare-amount"
      >
        {amountLabel}
      </span>
      <ChevronUp className="hidden sm:block w-3.5 h-3.5 text-[#9CA3AF]" />
    </button>
  );
}

export default FloatingFarePill;
